// Angular
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
// RxJS
import { Subscription } from 'rxjs';
// Services and Models
import { AgreementTemplate } from 'src/app/core/agreement';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AgreementService } from 'src/app/core/agreement/_services';
import { TemplateEditDialogComponent } from './template-edit-dialog/template-edit-dialog';
import { AgreementType } from 'src/app/core/agreement/_models/agreement-type.model';

@Component({
  selector: 'kt-template-edit',
  templateUrl: './template-edit.component.html',
  styleUrls: ['./template-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AgreementTemplateEditComponent implements OnInit, OnDestroy {
  form: FormGroup;
  isEdit: boolean;
  template: AgreementTemplate;
  templateId: number;
  templateName: string;
  hasFormErrors = false;
  hasSubmissionErrors = false;
  submissionErrorObj: any;
  viewLoading = true;
  loadingAfterSubmit = false;
  subscriptions: Subscription[] = [];
  templateTypes: AgreementType[];
  offices: number[] = [];
  userPrimaryOfficeId: number;

  /**
   * Component constructor
   *
   * @param dialogRef
   * @param data
   * @param dialog
   * @param agreementService
   */
  constructor(
    public dialogRef: MatDialogRef<AgreementTemplateEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private agreementService: AgreementService,
  ) {}

  /**
   * On init
   */
  ngOnInit() {
    this.viewLoading = false;
    this.templateId = this.data.template.id;
    this.isEdit = this.data.isEdit;
    this.templateTypes = this.data.templateTypes;
    this.userPrimaryOfficeId = this.data.userPrimaryOfficeId;

    this.offices = this.data.offices;

    let templateType: number;

    if (this.isEdit) {
      templateType = this.templateTypes.filter((t) => t.name === this.data.template.type)[0].id;
    }

    this.form = new FormGroup({
      name: new FormControl(
        { value: this.isEdit ? this.data.template.name : '', disabled: false },
        Validators.required,
      ),
      description: new FormControl(
        { value: this.isEdit ? this.data.template.description : '', disabled: false },
        Validators.required,
      ),
      fileName: new FormControl(''),
      templateType: new FormControl(
        { value: this.isEdit ? templateType : '', disabled: this.isEdit },
        Validators.required,
      ),
      offices: new FormControl(
        { value: this.isEdit ? [...this.data.template.offices] : [], disabled: false },
        Validators.required,
      ),
      file: new FormControl('', Validators.required),
    });

    this.template = new AgreementTemplate();
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Returns template for save
   */
  prepareAgreementTemplate(): AgreementTemplate {
    const controls = this.form.controls;

    const template = new AgreementTemplate();

    template.id = this.templateId;
    template.name = controls.name.value;
    template.description = controls.description.value;
    template.type = controls.templateType.value;
    template.offices = controls.offices.value;
    template.file_base64 = this.form.get('file').value;

    return template;
  }

  /**
   * Save data
   */
  onSubmit() {
    this.hasFormErrors = false;
    this.loadingAfterSubmit = false;

    const editedTemplate = this.prepareAgreementTemplate();

    this.upsertAgreementTemplate(editedTemplate);
  }

  upsertAgreementTemplate(template: AgreementTemplate) {
    this.loadingAfterSubmit = true;
    this.viewLoading = true;

    const action = template.id > 0 ? 'update' : 'create';

    const actionMethod = action === 'update' ? 'updateTemplate' : 'createTemplate';

    this.agreementService[actionMethod](template, this.userPrimaryOfficeId).subscribe((res) => {
      this.viewLoading = false;

      if (!res) return;

      const config = {
        maxWidth: '90vw',
        maxHeight: '90vh',
        height: '100%',
        width: '100%',
        data: { iframeSrc: res.extra_data.url },
      };

      if (res.signing_provider_name.toLowerCase() == 'boldsign') {
        this.dialog
          .open(TemplateEditDialogComponent, config)
          .afterClosed()
          .subscribe((res) => {
            this.viewLoading = false;

            if (res) this.dialogRef.close(true);
          });
      }
    });
  }

  /**
   * Close alert
   *
   */
  onAlertClose() {
    this.hasFormErrors = false;
  }

  /** UI */
  /**
   * Returns component title
   */
  getTitle(): string {
    if (this.template && this.template.id) {
      // tslint:disable-next-line:no-string-throw
      return 'AGREEMENT_TEMPLATE.EDIT_TEMPLATE';
    }

    // tslint:disable-next-line:no-string-throw
    return 'AGREEMENT_TEMPLATE.NEW_TEMPLATE';
  }

  changeFile(event) {
    const target = event.target as HTMLInputElement;

    if (target?.files?.length) {
      const file = target.files[0];
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.form.get('file').patchValue((reader.result as string)?.split(',')[1]);
        this.form.get('fileName').patchValue(file.name.replace(/\.[0-9a-z]+$/i, ''));
      };
    }
  }
}
